import React, { useRef, useState } from "react"
import { useSortable } from "@dnd-kit/react/sortable"
import type { Step, StepConfig, SelectOption, VariableGroups } from "./types"
import {
  STEP_TYPE_OPTIONS,
  FAILURE_POLICY_OPTIONS,
  INPUT_CLASS,
} from "./constants"
import { GripIcon, TrashIcon } from "./icons"
import { ForwardConfig } from "./ForwardConfig"
import { NotifyConfig } from "./NotifyConfig"
import { TagConfig } from "./TagConfig"
import { IgnoreConfig } from "./IgnoreConfig"
import { TransformConfig } from "./TransformConfig"
import { HttpRequestConfig } from "./HttpRequestConfig"
import { StepConditionsEditor } from "./StepConditionsEditor"

interface StepCardProps {
  step: Step
  index: number
  displayIndex: number
  sortableId: string
  onUpdate: (index: number, updates: Partial<Step>) => void
  onUpdateConfig: (index: number, updates: Partial<StepConfig>) => void
  onChangeType: (index: number, newType: string) => void
  onRemove: (index: number) => void
  channels: SelectOption[]
  tags: SelectOption[]
  ignoreReasons: SelectOption[]
  canForward: boolean
  canComposeActions: boolean
  canExecuteHttpRequest: boolean
  billingUrl: string
  variableHints?: VariableGroups
}

export function StepCard({
  step,
  index,
  displayIndex,
  sortableId,
  onUpdate,
  onUpdateConfig,
  onChangeType,
  onRemove,
  channels,
  tags,
  ignoreReasons,
  canForward,
  canComposeActions,
  canExecuteHttpRequest,
  billingUrl,
  variableHints,
}: StepCardProps) {
  const [element, setElement] = useState<Element | null>(null)
  const handleRef = useRef<HTMLDivElement>(null)
  const conditionsLocked =
    (step.step_type === "transform" && !canComposeActions) ||
    (step.step_type === "http_request" && !canExecuteHttpRequest)

  const { isDragging } = useSortable({
    id: sortableId,
    index: displayIndex,
    element,
    handle: handleRef,
  })

  return (
    <div
      ref={setElement}
      className={`border border-gray-200 rounded-lg p-3 mb-2 bg-white transition-opacity ${
        isDragging ? "opacity-50 shadow-lg" : ""
      }`}
      data-testid={`step-card-${displayIndex}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div
            ref={handleRef}
            className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500"
          >
            <GripIcon />
          </div>
          <span className="text-xs font-medium text-gray-500 uppercase">
            Step {displayIndex + 1}
          </span>
          {step.id && step.reference_key && (
            <span className="font-mono text-xs text-gray-400" title="This key is immutable after the step is created">
              {step.reference_key}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="p-1 text-gray-400 hover:text-red-600"
          aria-label="Remove step"
        >
          <TrashIcon />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-3">
        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-700">
            Type
          </label>
          <select
            value={step.step_type}
            onChange={(e) => onChangeType(index, e.target.value)}
            className={INPUT_CLASS}
          >
            {!step.step_type && <option value="">Choose type...</option>}
            {STEP_TYPE_OPTIONS.map((opt) => (
              <option
                key={opt.value}
                value={opt.value}
                disabled={(opt.value === "forward" && !canForward) || (opt.value === "transform" && !canComposeActions) || (opt.value === "http_request" && !canExecuteHttpRequest)}
              >
                {opt.value === "forward"
                  ? "Forward (Paid)"
                  : opt.value === "transform"
                    ? "Transform (Business)"
                  : opt.value === "http_request"
                    ? "HTTP Request (Business)"
                    : opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-700">
            Name
          </label>
          <input
            type="text"
            value={step.name}
            onChange={(e) => onUpdate(index, { name: e.target.value })}
            className={INPUT_CLASS}
            placeholder="Step name"
          />
        </div>
        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-700">
            On Failure
          </label>
          <select
            value={step.failure_policy || ""}
            onChange={(e) =>
              onUpdate(index, { failure_policy: e.target.value || null })
            }
            className={INPUT_CLASS}
          >
            {FAILURE_POLICY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-3">
        {step.step_type === "forward" && (
          <ForwardConfig
            config={step.config}
            disabled={!canForward}
            onChange={(updates) => onUpdateConfig(index, updates)}
          />
        )}
        {step.step_type === "notify" && (
          <NotifyConfig
            config={step.config}
            channels={channels}
            onChange={(updates) => onUpdateConfig(index, updates)}
          />
        )}
        {step.step_type === "tag" && (
          <TagConfig
            config={step.config}
            tags={tags}
            onChange={(updates) => onUpdateConfig(index, updates)}
          />
        )}
        {step.step_type === "ignore" && (
          <IgnoreConfig
            config={step.config}
            ignoreReasons={ignoreReasons}
            onChange={(updates) => onUpdateConfig(index, updates)}
          />
        )}
        {step.step_type === "transform" && (
          <>
            <TransformConfig config={step.config} variableHints={variableHints} disabled={!canComposeActions} onChange={(updates) => onUpdateConfig(index, updates)} />
            {!canComposeActions && (
              <p className="text-xs text-amber-700" role="note">
                Transform and step composition are available on Business. <a href={billingUrl} className="font-medium underline hover:text-amber-900">Compare Plans</a>
              </p>
            )}
          </>
        )}
        {step.step_type === "http_request" && (
          <>
            <HttpRequestConfig config={step.config} variableHints={variableHints} disabled={!canExecuteHttpRequest} onChange={(updates) => onUpdateConfig(index, updates)} />
            {!canExecuteHttpRequest && (
              <p className="text-xs text-amber-700" role="note">
                HTTP Request requires Business and an active paid subscription. <a href={billingUrl} className="font-medium underline hover:text-amber-900">Manage Billing</a>
              </p>
            )}
          </>
        )}
        {step.step_type && (
          <StepConditionsEditor conditions={step.conditions || []} disabled={conditionsLocked} onChange={(conditions) => onUpdate(index, { conditions })} />
        )}
      </div>
    </div>
  )
}
